import { memo, useEffect, useMemo, useState } from 'react'

import { PartnerAttachments } from 'clickNgo/entities/partner/ui/partnerAttachments'
import { InfoBlock, InfoBlockType } from 'cng-common/components/infoBlock'
import { RenderWhen } from 'cng-common/components/renderWhen'
import { Translation } from 'cng-common/features/translations/components'

import { PARTNER_EDITOR_FORM_KEYS, PartnerEditorFormKeys } from '../../../configs'
import { useReadOnlyMenuItem } from '../../../hooks/useReadOnlyMenuItem'
import { PartnerFormEditableContainer } from '../../partnerFormEditableContainer/PartnerFormEditableContainer'

import { useEditPartnerAttachmentsHandler } from './hooks/useEditPartnerAttachmentsHandler'
import { usePartnerAttachmentsValidation } from './hooks/usePartnerAttachmentsValidation'
import { IWrappedPartnerAttachmentsProps } from './models'

export const PARTNER_LEGAL_DETAILS_LINK =
  'https://hp.sharepoint.com/:b:/r/teams/Amplifyops/Documentation/Documentation%20to%20be%20provided%20when%20legal%20address%20change%20is%20requested%20for%20a%20contracted%20partner/Documentation%20to%20be%20provided%20when%20legal%20address%20change%20is%20requested%20for%20a%20contracted%20partner%20.pdf?csf=1&web=1&e=P6MsbY'

const mandatoryLegalAttachmentsInfo = (
  <a target="_blank" href={PARTNER_LEGAL_DETAILS_LINK} rel="noreferrer">
    <Translation text="partner-legal-see-required-documents-list" span />
  </a>
)

export const WrappedPartnerAttachments = memo<IWrappedPartnerAttachmentsProps>(
  ({ partner, partnerAttachments }) => {
    const { isCurrentMenuItemReadOnly } = useReadOnlyMenuItem()
    const [nonUploadedAttachmentsMessage, setNonUploadedAttachmentsMessage] = useState('')
    const { onAttachmentsValidate } = usePartnerAttachmentsValidation({
      formKeys: PARTNER_EDITOR_FORM_KEYS,
    })

    const {
      handleOnPartnerAttachmentsChangesApply,
      handleOnPartnerAttachmentsChangesCancel,
      notUploadedAttachments,
    } = useEditPartnerAttachmentsHandler({ partnerId: partner.partnerId })

    const renderNonUploadedAttachmentsMessage = useMemo(
      () => <span>{nonUploadedAttachmentsMessage}</span>,
      [nonUploadedAttachmentsMessage]
    )

    useEffect(() => {
      if (notUploadedAttachments.length) {
        setNonUploadedAttachmentsMessage(
          `Some files could not be uploaded: ${notUploadedAttachments.join(', ')}`
        )
      } else {
        setNonUploadedAttachmentsMessage('')
      }
    }, [notUploadedAttachments])

    return (
      <PartnerFormEditableContainer
        partner={partner}
        formKey={PartnerEditorFormKeys.Attachments}
        title={PartnerEditorFormKeys.Attachments}
        onApply={handleOnPartnerAttachmentsChangesApply}
        onCancel={handleOnPartnerAttachmentsChangesCancel}
      >
        <RenderWhen condition={partner.hasAgreements}>
          <InfoBlock text={mandatoryLegalAttachmentsInfo} type={InfoBlockType.Help} />
        </RenderWhen>
        <RenderWhen
          condition={!isCurrentMenuItemReadOnly && Boolean(nonUploadedAttachmentsMessage.length)}
        >
          <InfoBlock
            text={renderNonUploadedAttachmentsMessage}
            type={InfoBlockType.Danger}
            hasIcon
          />
        </RenderWhen>
        <PartnerAttachments
          {...partner}
          isReadOnly={isCurrentMenuItemReadOnly}
          attachments={partnerAttachments}
          onAttachmentsValidate={onAttachmentsValidate}
          formKeys={PARTNER_EDITOR_FORM_KEYS}
        />
      </PartnerFormEditableContainer>
    )
  }
)
